// ============================================================
//  ExploreContent REDESIGN — Graia Premium Cards
//  SUBSTITUI: ExploreContent.tsx (componente principal)
//  NOVO: ícones Lucide, menu ⋮, favoritos, tópicos customizados,
//        cores por campo, faixa superior, hover premium
// ============================================================

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomStudy from './CustomStudy';
import UpgradeModal from './UpgradeModal';
import AccessControl from '../AccessControl/AccessControl';
import InterdictedModal from '../AccessControl/InterdictedModal';
import SavedFoldersView from './SavedFoldersView';

import {
    Plus, Edit, Trash2, Eye, EyeOff, GripVertical, BadgeCheck,
    MoreVertical, Star,
    // ── Ícones de matérias ENEM / Escola ──
    BookOpen, PenLine, FileText, Languages, Quote, NotebookPen,
    Atom, Zap, Activity, FlaskConical, TestTube2, Orbit, Magnet,
    Dna, Microscope, Brain, Leaf, Bug,
    Hash, BarChart2, TrendingUp, Shapes, Calculator,
    Globe, Map, Landmark, Clock, ScrollText, Mountain,
    Scale, Lightbulb, Users, UserRound,
    Palette, Music, Theater,
    GraduationCap, Award, Search, Layers, ClipboardList, AlertCircle,
    X, ArrowRight,
} from 'lucide-react';

import {
    DndContext, closestCenter, KeyboardSensor,
    PointerSensor, useSensor, useSensors
} from '@dnd-kit/core';
import {
    SortableContext, sortableKeyboardCoordinates,
    useSortable, rectSortingStrategy, verticalListSortingStrategy, arrayMove
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Cycle, SavedCard } from './types';
import { ExploreSubTab } from './Sidebar';
import { useAuth } from '../../contexts/AuthContext';
import './ExploreContent.css';

const API_BASE_URL = '/api';

// ── Mapa de ícones disponíveis (Lucide) para o admin escolher ──
export const CYCLE_ICONS: Record<string, React.ComponentType<any>> = {
    // Português / Literatura / Redação
    BookOpen,
    PenLine,
    FileText,
    Quote,
    NotebookPen,
    // Línguas estrangeiras
    Languages,
    // Matemática
    Hash,
    BarChart2,
    TrendingUp,
    Shapes,
    Calculator,
    // Física
    Zap,
    Orbit,
    Magnet,
    Activity,
    // Química
    FlaskConical,
    TestTube2,
    Atom,
    // Biologia
    Dna,
    Microscope,
    Leaf,
    Bug,
    Brain,
    // História
    Landmark,
    Clock,
    ScrollText,
    // Geografia
    Globe,
    Map,
    Mountain,
    // Filosofia
    Lightbulb,
    Scale,
    // Sociologia
    Users,
    UserRound,
    // Artes
    Palette,
    Music,
    Theater,
    // Geral / ENEM
    GraduationCap,
    Award,
    Layers,
    ClipboardList,
    Search,
    AlertCircle,
};

// Labels para tooltip (não exibidos no picker)
export const CYCLE_ICON_LABELS: Record<string, string> = {
    // Português / Literatura / Redação
    BookOpen: 'Português / Literatura',
    PenLine: 'Redação / Escrita',
    FileText: 'Texto / Produção',
    Quote: 'Interpretação de Texto',
    NotebookPen: 'Redação / Anotações',
    // Línguas estrangeiras
    Languages: 'Línguas Estrangeiras',
    // Matemática
    Hash: 'Matemática',
    BarChart2: 'Estatística / Probabilidade',
    TrendingUp: 'Funções / Gráficos',
    Shapes: 'Geometria / Formas',
    Calculator: 'Cálculo / Álgebra',
    // Física
    Zap: 'Física / Eletricidade',
    Orbit: 'Física / Mecânica Orbital',
    Magnet: 'Física / Eletromagnetismo',
    Activity: 'Física / Ondas',
    // Química
    FlaskConical: 'Química',
    TestTube2: 'Química / Laboratório',
    Atom: 'Química / Física Moderna',
    // Biologia
    Dna: 'Biologia / Genética',
    Microscope: 'Biologia / Laboratório',
    Leaf: 'Biologia / Ecologia',
    Bug: 'Biologia / Zoologia',
    Brain: 'Biologia / Neurociências',
    // História
    Landmark: 'História / Patrimônio',
    Clock: 'História / Cronologia',
    ScrollText: 'História / Documentos',
    // Geografia
    Globe: 'Geografia / Geopolítica',
    Map: 'Geografia / Cartografia',
    Mountain: 'Geografia / Geomorfologia',
    // Filosofia
    Lightbulb: 'Filosofia / Ideias',
    Scale: 'Filosofia / Ética',
    // Sociologia
    Users: 'Sociologia / Grupos Sociais',
    UserRound: 'Sociologia / Humanidades',
    // Artes
    Palette: 'Artes Visuais',
    Music: 'Música',
    Theater: 'Artes Cênicas / Teatro',
    // Geral / ENEM
    GraduationCap: 'ENEM / Vestibular',
    Award: 'Destaque',
    Layers: 'Múltiplas Áreas',
    ClipboardList: 'Resumo / Anotações',
    Search: 'Pesquisa / Revisão',
    AlertCircle: 'Pontos de Atenção',
};

// Grupos de ícones por matéria — usados pelo IconPicker
const ICON_GROUPS: { id: string; label: string; keys: string[] }[] = [
    { id: 'portugues',  label: 'Português',  keys: ['BookOpen', 'PenLine', 'FileText', 'Quote', 'NotebookPen'] },
    { id: 'linguas',    label: 'Línguas',    keys: ['Languages'] },
    { id: 'matematica', label: 'Matemática', keys: ['Hash', 'BarChart2', 'TrendingUp', 'Shapes', 'Calculator'] },
    { id: 'fisica',     label: 'Física',     keys: ['Zap', 'Orbit', 'Magnet', 'Activity'] },
    { id: 'quimica',    label: 'Química',    keys: ['FlaskConical', 'TestTube2', 'Atom'] },
    { id: 'biologia',   label: 'Biologia',   keys: ['Dna', 'Microscope', 'Leaf', 'Bug', 'Brain'] },
    { id: 'historia',   label: 'História',   keys: ['Landmark', 'Clock', 'ScrollText'] },
    { id: 'geografia',  label: 'Geografia',  keys: ['Globe', 'Map', 'Mountain'] },
    { id: 'filosofia',  label: 'Filosofia',  keys: ['Lightbulb', 'Scale'] },
    { id: 'sociologia', label: 'Sociologia', keys: ['Users', 'UserRound'] },
    { id: 'artes',      label: 'Artes',      keys: ['Palette', 'Music', 'Theater'] },
    { id: 'geral',      label: 'Geral',      keys: ['GraduationCap', 'Award', 'Layers', 'ClipboardList', 'Search', 'AlertCircle'] },
];

// Legacy export — mantido para compatibilidade com src/user-creator/ e src/ryan/
export const CYCLE_COLORS = [
    { id: 'blue', label: 'Azul', top: '#6b9fd4', bg: 'rgba(107,159,212,0.22)', border: 'rgba(107,159,212,0.18)' },
    { id: 'emerald', label: 'Verde', top: '#2F7130', bg: 'rgba(47,113,48,0.22)', border: 'rgba(47,113,48,0.18)' },
    { id: 'violet', label: 'Roxo', top: '#8b75c9', bg: 'rgba(139,117,201,0.22)', border: 'rgba(139,117,201,0.18)' },
    { id: 'rose', label: 'Vermelho', top: '#c47a7a', bg: 'rgba(196,122,122,0.22)', border: 'rgba(196,122,122,0.18)' },
    { id: 'amber', label: 'Âmbar', top: '#c49a4a', bg: 'rgba(196,154,74,0.22)', border: 'rgba(196,154,74,0.18)' },
    { id: 'cyan', label: 'Ciano', top: '#5a9db5', bg: 'rgba(90,157,181,0.22)', border: 'rgba(90,157,181,0.18)' },
    { id: 'pink', label: 'Rosa', top: '#c47a9a', bg: 'rgba(196,122,154,0.22)', border: 'rgba(196,122,154,0.18)' },
    { id: 'orange', label: 'Laranja', top: '#c48a5a', bg: 'rgba(196,138,90,0.22)', border: 'rgba(196,138,90,0.18)' },
    { id: 'teal', label: 'Teal', top: '#4a9e8e', bg: 'rgba(74,158,142,0.22)', border: 'rgba(74,158,142,0.18)' },
    { id: 'indigo', label: 'Índigo', top: '#6b7fc4', bg: 'rgba(107,127,196,0.22)', border: 'rgba(107,127,196,0.18)' },
    { id: 'green1', label: 'Verde Claro', top: '#5a9e6f', bg: 'rgba(90,158,111,0.22)', border: 'rgba(90,158,111,0.18)' },
];

// Paleta de cores para áreas
export const AREA_COLORS: { hex: string; label: string }[] = [
    // ── Azuis GRAIA (foco principal) ──
    { hex: '#003BD1', label: 'Azul Vibrante GRAIA' },
    { hex: '#042563', label: 'Azul Profundo GRAIA' },
    { hex: '#0F172A', label: 'Azul Quase Preto' },
    { hex: '#1E3A5F', label: 'Azul Noite' },
    { hex: '#1E40AF', label: 'Azul Royal' },
    { hex: '#1D4ED8', label: 'Azul Vivo' },
    { hex: '#2563EB', label: 'Azul Médio' },
    { hex: '#2E4DB0', label: 'Azul Cobalto' },
    { hex: '#3B82F6', label: 'Azul Claro' },
    { hex: '#4B6BCC', label: 'Periwinkle' },
    { hex: '#6B7FC4', label: 'Índigo Azulado' },
    { hex: '#60A5FA', label: 'Azul Céu' },
    // ── Azuis Frios / Petróleo ──
    { hex: '#075985', label: 'Azul Marinho' },
    { hex: '#0369A1', label: 'Azul Petróleo Escuro' },
    { hex: '#0284C7', label: 'Azul Petróleo' },
    { hex: '#0EA5E9', label: 'Azul Ciano Vivo' },
    { hex: '#38BDF8', label: 'Azul Céu Claro' },
    { hex: '#06B6D4', label: 'Ciano' },
    { hex: '#0891B2', label: 'Ciano Escuro' },
    { hex: '#164E63', label: 'Teal Noite' },
    { hex: '#0F766E', label: 'Teal' },
    { hex: '#6b8fa8', label: 'Azul Aço' },
    { hex: '#6a7f8e', label: 'Azul Ardósia' },
    // ── Verdes GRAIA ──
    { hex: '#2F7130', label: 'Verde GRAIA Clássico' },
    { hex: '#1e5c2e', label: 'Verde Floresta Escuro' },
    { hex: '#166534', label: 'Verde Profundo' },
    { hex: '#15803D', label: 'Verde Vibrante' },
    { hex: '#16A34A', label: 'Verde Médio' },
    { hex: '#22C55E', label: 'Verde Claro' },
    { hex: '#2d8a5e', label: 'Verde Esmeralda' },
    { hex: '#00a86b', label: 'Verde Jade' },
    { hex: '#4aaa8a', label: 'Verde Menta' },
    { hex: '#40826d', label: 'Verde Viridiano' },
    { hex: '#1b4332', label: 'Verde Brunswick' },
    { hex: '#4f7942', label: 'Verde Samambaia' },
    { hex: '#29ab48', label: 'Verde Selva' },
    { hex: '#6b8e23', label: 'Verde Lima' },
    // ── Outros ──
    { hex: '#7C3AED', label: 'Roxo Vivo' },
    { hex: '#8b75c9', label: 'Roxo Suave' },
    { hex: '#6D28D9', label: 'Violeta' },
    { hex: '#9333EA', label: 'Púrpura' },
    { hex: '#DB2777', label: 'Rosa' },
    { hex: '#c47a9a', label: 'Rosa Suave' },
    { hex: '#DC2626', label: 'Vermelho' },
    { hex: '#c47a7a', label: 'Vermelho Suave' },
    { hex: '#D97706', label: 'Âmbar' },
    { hex: '#c49a4a', label: 'Âmbar Suave' },
    { hex: '#EA580C', label: 'Laranja' },
    { hex: '#c48a5a', label: 'Laranja Suave' },
    { hex: '#64748B', label: 'Cinza' },
];

// ── Hook para favoritos (localStorage) ──
function useFavorites() {
    const KEY = 'graia_fav_cycles';
    const [favs, setFavs] = useState<string[]>(() => {
        try { return JSON.parse(localStorage.getItem(KEY) || '[]'); }
        catch { return []; }
    });
    const toggle = (id: string) => {
        setFavs(prev => {
            const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id].slice(-3);
            localStorage.setItem(KEY, JSON.stringify(next));
            return next;
        });
    };
    return { favs, toggle };
}

// ── Menu ⋮ flutuante ──
interface CardMenuProps {
    cycleId: string;
    isBlocked: boolean;
    isHidden: boolean;
    onEdit: () => void;
    onDelete: () => void;
    onToggleBlock: () => void;
    onToggleHide: () => void;
}
const CardMenu: React.FC<CardMenuProps> = ({ cycleId, isBlocked, isHidden, onEdit, onDelete, onToggleBlock, onToggleHide }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
        <div className="gcr-menu-wrap" ref={ref}>
            <button
                className="gcr-menu-btn"
                onClick={e => { e.stopPropagation(); setOpen(v => !v); }}
                title="Opções"
            >
                <MoreVertical size={16} />
            </button>
            {open && (
                <div className="gcr-menu-dropdown" onClick={e => e.stopPropagation()}>
                    <button onClick={() => { onToggleBlock(); setOpen(false); }}>
                        {isBlocked ? <Eye size={14} /> : <EyeOff size={14} />}
                        {isBlocked ? 'Disponibilizar' : 'Interditar'}
                    </button>
                    <button
                        className={isHidden ? 'gcr-menu-hidden-active' : ''}
                        onClick={() => { onToggleHide(); setOpen(false); }}
                    >
                        {isHidden ? <Eye size={14} /> : <EyeOff size={14} />}
                        {isHidden ? 'Mostrar' : 'Esconder'}
                    </button>
                    <button onClick={() => { onEdit(); setOpen(false); }}>
                        <Edit size={14} />Editar
                    </button>
                    <button className="gcr-menu-danger" onClick={() => { onDelete(); setOpen(false); }}>
                        <Trash2 size={14} />Deletar
                    </button>
                </div>
            )}
        </div>
    );
};

// ── Seletor de Ícone ──
const IconPicker: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => (
    <div className="icon-picker">
        {ICON_GROUPS.map(g => (
            <div key={g.id} className="icon-picker__section">
                <p className="icon-picker__section-title">{g.label}</p>
                <div className="icon-picker__icons">
                    {g.keys.map(key => {
                        const Icon = CYCLE_ICONS[key];
                        if (!Icon) return null;
                        return (
                            <button
                                key={key}
                                type="button"
                                className={`gcr-icon-opt ${value === key ? 'selected' : ''}`}
                                onClick={() => onChange(key)}
                                title={CYCLE_ICON_LABELS[key] || key}
                            >
                                <Icon size={20} />
                            </button>
                        );
                    })}
                </div>
            </div>
        ))}
    </div>
);

// ── Seletor de Cor para Áreas ──
const AreaColorPicker: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => (
    <div className="area-color-dot-picker">
        {AREA_COLORS.map(c => (
            <button
                key={c.hex}
                type="button"
                className={`area-color-dot-btn ${value === c.hex ? 'selected' : ''}`}
                style={{ background: c.hex }}
                onClick={() => onChange(c.hex)}
                title={c.label}
            />
        ))}
    </div>
);

// ── Item de área arrastável (Gerenciar Áreas) ──
interface SortableAreaItemProps {
    area: { _id: string; name: string; color: string; cycles: any[] };
    onEdit: () => void;
    onDelete: () => void;
}
const SortableAreaItem: React.FC<SortableAreaItemProps> = ({ area, onEdit, onDelete }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: area._id });
    const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
    return (
        <div ref={setNodeRef} style={style} className="manage-area-item">
            <div className="manage-area-drag" {...attributes} {...listeners}>
                <GripVertical size={16} />
            </div>
            <div className="manage-area-color-dot" style={{ background: area.color }} />
            <div className="manage-area-info">
                <span className="manage-area-name">{area.name}</span>
                <span className="manage-area-count">{area.cycles.length} campo{area.cycles.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="manage-area-actions">
                <button onClick={onEdit} title="Editar"><Edit size={14} /></button>
                <button onClick={onDelete} className="danger" title="Excluir"><Trash2 size={14} /></button>
            </div>
        </div>
    );
};

// ── Editor de Tópicos Graia ──
interface Topic { id: string; text: string; tag: string; }
const TOPIC_TAGS = [
    { id: 'destaque', label: 'Destaque', Icon: Star, color: '#f59e0b' },
    { id: 'area', label: 'Área', Icon: Brain, color: '#6366f1' },
    { id: 'nivel', label: 'Nível', Icon: BarChart2, color: '#10b981' },
    { id: 'info', label: 'Info', Icon: FileText, color: '#3b82f6' },
    { id: 'alerta', label: 'Atencao', Icon: AlertCircle, color: '#ef4444' },
];

const TopicsEditor: React.FC<{ value: Topic[]; onChange: (v: Topic[]) => void }> = ({ value, onChange }) => {
    const add = () => {
        if (value.length >= 5) return;
        onChange([...value, { id: Date.now().toString(), text: '', tag: 'info' }]);
    };
    const remove = (id: string) => onChange(value.filter(t => t.id !== id));
    const update = (id: string, field: keyof Topic, val: string) =>
        onChange(value.map(t => t.id === id ? { ...t, [field]: val } : t));

    return (
        <div className="gcr-topics-editor">
            <div className="gcr-topics-label">
                <Hash size={14} />Tópicos do card <span>(máx. 5)</span>
            </div>
            {value.map(t => (
                <div key={t.id} className="gcr-topic-row">
                    <select
                        value={t.tag}
                        onChange={e => update(t.id, 'tag', e.target.value)}
                        className="gcr-topic-tag-sel"
                    >
                        {TOPIC_TAGS.map(tg => (
                            <option key={tg.id} value={tg.id}>{tg.label}</option>
                        ))}
                    </select>
                    <input
                        value={t.text}
                        onChange={e => update(t.id, 'text', e.target.value)}
                        placeholder="Ex: 12 decks de anatomia sistêmica"
                        className="gcr-topic-input"
                    />
                    <button className="gcr-topic-remove" onClick={() => remove(t.id)} type="button">
                        <X size={14} />
                    </button>
                </div>
            ))}
            {value.length < 5 && (
                <button className="gcr-topic-add" onClick={add} type="button">
                    <Plus size={14} /> Adicionar tópico
                </button>
            )}
        </div>
    );
};

// ── Renderizar tópicos no card ──
const TopicBadge: React.FC<{ topic: Topic }> = ({ topic }) => {
    const tag = TOPIC_TAGS.find(t => t.id === topic.tag) || TOPIC_TAGS[3];
    const TagIcon = tag.Icon;
    return (
        <span className="gcr-topic-badge" style={{ '--tag-color': tag.color } as any}>
            <TagIcon size={11} />
            {topic.text}
        </span>
    );
};

// ── Cycle Card Premium ──
interface PremiumCycleCardProps {
    cycle: Cycle & {
        purchased?: boolean;
        icon?: string;
        colorId?: string;
        topics?: Topic[];
        isHidden?: boolean;
    };
    isAdmin: boolean;
    isFav: boolean;
    areaColor?: string;
    onFavToggle: () => void;
    onEdit: () => void;
    onDelete: () => void;
    onToggleBlock: () => void;
    onToggleHide: () => void;
}

const PremiumCycleCard: React.FC<PremiumCycleCardProps> = ({
    cycle, isAdmin, isFav, areaColor, onFavToggle, onEdit, onDelete, onToggleBlock, onToggleHide
}) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: cycle._id,
        disabled: !isAdmin
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.3 : 1,
    };

    const effectiveColor = areaColor ?? '#64748B';
    const IconComp = cycle.icon && CYCLE_ICONS[cycle.icon] ? CYCLE_ICONS[cycle.icon] : BookOpen;
    const topics: Topic[] = cycle.topics || [];
    const deckCount = cycle.deckCount ?? cycle.decks?.length ?? 0;

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`gcr-card ${isDragging ? 'dragging' : ''} ${isFav ? 'fav' : ''} ${cycle.isBlocked ? 'blocked' : ''} ${cycle.isHidden ? 'hidden-admin' : ''}`}
        >
            {/* Faixa superior colorida */}
            <div className="gcr-top-bar" style={{ background: effectiveColor }} />

            {/* Cabeçalho: ícone + título + ações */}
            <div className="gcr-head">
                <div className="gcr-head-left">
                    <div className="gcr-icon-wrap" style={{ background: `${effectiveColor}1A`, border: `1.5px solid ${effectiveColor}30` }}>
                        <IconComp size={24} style={{ color: effectiveColor }} />
                    </div>
                    <div className="gcr-title-wrap">
                        <h3 className="gcr-title">{cycle.name}</h3>
                        {isAdmin && cycle.isHidden && (
                            <span className="gcr-hidden-badge"><EyeOff size={11} />Oculto</span>
                        )}
                    </div>
                </div>
                <div className="gcr-head-actions">
                    {/* Favorito */}
                    <button
                        className={`gcr-fav-btn ${isFav ? 'active' : ''}`}
                        onClick={e => { e.stopPropagation(); onFavToggle(); }}
                        title={isFav ? 'Remover favorito' : 'Fixar campo'}
                    >
                        {isFav ? <Star size={15} fill="currentColor" /> : <Star size={15} />}
                    </button>
                    {/* Admin: drag + menu */}
                    {isAdmin && (
                        <>
                            <div
                                className="gcr-drag-handle"
                                {...attributes}
                                {...listeners}
                                onClick={e => e.stopPropagation()}
                                title="Arrastar para reorganizar"
                            >
                                <GripVertical size={16} />
                            </div>
                            <CardMenu
                                cycleId={cycle._id}
                                isBlocked={cycle.isBlocked || false}
                                isHidden={cycle.isHidden || false}
                                onEdit={onEdit}
                                onDelete={onDelete}
                                onToggleBlock={onToggleBlock}
                                onToggleHide={onToggleHide}
                            />
                        </>
                    )}
                </div>
            </div>

            {/* Corpo */}
            <div className="gcr-body">
                {cycle.description && (
                    <p className="gcr-desc">{cycle.description}</p>
                )}

                {/* Tópicos customizados */}
                {topics.length > 0 && (
                    <div className="gcr-topics">
                        {topics.filter(t => t.text).map(t => (
                            <TopicBadge key={t.id} topic={t} />
                        ))}
                    </div>
                )}

                {/* Stats */}
                <div className="gcr-stats" style={{ color: effectiveColor }}>
                    <span className="gcr-stat-num">{deckCount}</span>
                    <span className="gcr-stat-lbl">deck{deckCount !== 1 ? 's' : ''}</span>
                </div>
            </div>

            {/* Botão Entrar */}
            <div className="gcr-footer">
                <button
                    className="gcr-enter-btn"
                    style={{ background: effectiveColor }}
                    disabled={false}
                >
                    Entrar <ArrowRight size={15} />
                </button>
            </div>

        </div>
    );
};

// ============================================================
//  INTERFACE AREA
// ============================================================
interface Area {
    _id: string;
    name: string;
    order: number;
    color: string;
    gridCols: number;
    isVisible: boolean;
    cycles: (Cycle & { icon?: string; colorId?: string; topics?: Topic[]; isHidden?: boolean })[];
}

// ============================================================
//  SEPARADOR DE ÁREA
// ============================================================
const AreaSeparator: React.FC<{
    area: { name: string; color: string };
}> = ({ area }) => (
    <div className="area-separator">
        <div className="area-separator-line"
            style={{ background: `linear-gradient(to right, transparent, ${area.color}90, ${area.color})` }} />
        <div className="area-separator-content">
            <span className="area-separator-name"
                style={{ color: area.color, background: `${area.color}18`, borderColor: `${area.color}40` }}>
                {area.name}
            </span>
        </div>
        <div className="area-separator-line"
            style={{ background: `linear-gradient(to left, transparent, ${area.color}90, ${area.color})` }} />
    </div>
);

// ============================================================
//  COMPONENTE PRINCIPAL
// ============================================================
interface ExploreContentProps {
    cycles: Cycle[];
    savedCards: SavedCard[];
    isAdmin: boolean;
    isSavingOrder: boolean;
    hasRealPurchases: boolean;
    onCyclesChange: (cycles: Cycle[]) => void;
    onReloadCycles: () => void;
    onReloadSavedCards: () => void;
    onRemoveSavedCard: (cardId: string) => void;
    onToggleCycleBlock: (cycleId: string) => void;
    onToggleCycleHide: (cycleId: string) => void;
    onDragEnd: (event: any) => void;
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
    activeTab?: ExploreSubTab;
    onRegisterCreateCycle?: (fn: () => void) => void;
    onRegisterNewFolder?: (fn: () => void) => void;
    onRegisterCreateArea?: (fn: () => void) => void;
    onRegisterManageAreas?: (fn: () => void) => void;
}

const ExploreContent: React.FC<ExploreContentProps> = ({
    cycles,
    savedCards,
    isAdmin,
    hasRealPurchases,
    onReloadCycles,
    onReloadSavedCards,
    onRemoveSavedCard,
    onToggleCycleBlock,
    onToggleCycleHide,
    onDragEnd,
    onShowToast,
    activeTab = 'content',
    onRegisterCreateCycle,
    onRegisterNewFolder,
    onRegisterCreateArea,
    onRegisterManageAreas,
}) => {
    const navigate = useNavigate();
    const { favs, toggle: toggleFav } = useFavorites();
    const { isEditor } = useAuth();

    // ── Dialogs (cycles) ──
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [selectedCycle, setSelectedCycle] = useState<Cycle | null>(null);
    const [showInterdictedModal, setShowInterdictedModal] = useState(false);
    const [selectedEntityForModal, setSelectedEntityForModal] = useState<{ type: 'cycle' | 'deck'; name: string } | null>(null);

    // ── Form state (cycles) ──
    const [cycleName, setCycleName] = useState('');
    const [cycleDescription, setCycleDescription] = useState('');
    const [cycleIsFree, setCycleIsFree] = useState(false);
    const [cycleSkipDeckLevel, setCycleSkipDeckLevel] = useState(false);
    const [cycleIcon, setCycleIcon] = useState('BookOpen');
    const [cycleTopics, setCycleTopics] = useState<Topic[]>([]);
    const [cycleAreaId, setCycleAreaId] = useState('');
    const [currentCycle, setCurrentCycle] = useState<Cycle | null>(null);

    // ── Estado de áreas ──
    const [areas, setAreas] = useState<Area[]>([]);
    const [cyclesWithoutArea, setCyclesWithoutArea] = useState<Cycle[]>([]);
    const [loadingAreas, setLoadingAreas] = useState(true);

    // ── Dialogs (áreas) ──
    const [showManageAreasModal, setShowManageAreasModal] = useState(false);
    const [showCreateAreaDialog, setShowCreateAreaDialog] = useState(false);
    const [showEditAreaDialog, setShowEditAreaDialog] = useState(false);
    const [showDeleteAreaDialog, setShowDeleteAreaDialog] = useState(false);
    const [currentArea, setCurrentArea] = useState<Area | null>(null);

    // ── Form state (áreas) ──
    const [areaFormName, setAreaFormName] = useState('');
    const [areaFormColor, setAreaFormColor] = useState('#003BD1');
    const [areaFormGridCols, setAreaFormGridCols] = useState<2 | 3>(2);
    const [areaFormIsVisible, setAreaFormIsVisible] = useState(true);
    const [areaFormOrder, setAreaFormOrder] = useState(0);
    const [addCycleSelectedId, setAddCycleSelectedId] = useState('');


    // Registra handlers no DashboardPage para que o header externo possa acionar dialogs internos
    useEffect(() => {
        onRegisterCreateCycle?.(() => setShowCreateDialog(true));
        onRegisterCreateArea?.(() => setShowCreateAreaDialog(true));
        onRegisterManageAreas?.(() => setShowManageAreasModal(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Carrega áreas na montagem do componente
    useEffect(() => {
        fetchAreas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    // ── Ordenar: fixados primeiro, depois ordem normal ──
    const sortedCycles = [...cycles].sort((a, b) => {
        const aFav = favs.includes(a._id) ? 0 : 1;
        const bFav = favs.includes(b._id) ? 0 : 1;
        if (aFav !== bFav) return aFav - bFav;
        return (a.order || 0) - (b.order || 0);
    });

    const resetForm = () => {
        setCycleName(''); setCycleDescription(''); setCycleIsFree(false);
        setCycleSkipDeckLevel(false);
        setCycleIcon('BookOpen'); setCycleTopics([]); setCycleAreaId('');
    };

    const clearCyclesCache = () => {
        sessionStorage.removeItem('dashboard_cycles');
        Object.keys(sessionStorage).filter(k => k.startsWith('cycleDecks_'))
            .forEach(k => sessionStorage.removeItem(k));
    };

    const handleCreateCycle = async () => {
        if (!cycleName.trim()) { onShowToast('Digite um nome', 'error'); return; }
        try {
            const token = localStorage.getItem('accessToken');
            const nextOrder = cycles.length > 0 ? Math.max(...cycles.map(c => c.order || 0)) + 1 : 1;
            const response = await fetch(`${API_BASE_URL}/cycles`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    name: cycleName.trim(),
                    description: cycleDescription.trim(),
                    order: nextOrder,
                    isFree: cycleIsFree,
                    skipDeckLevel: cycleSkipDeckLevel,
                    icon: cycleIcon,
                    topics: cycleTopics.filter(t => t.text.trim()),
                })
            });
            if (!response.ok) {
                const err = await response.json();
                onShowToast(err.message || 'Erro ao criar campo', 'error'); return;
            }
            const newCycle = await response.json();
            // Assignar à área se selecionada
            if (cycleAreaId && newCycle._id) {
                await fetch(`${API_BASE_URL}/areas/${cycleAreaId}/add-cycle`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ cycleId: newCycle._id })
                });
            }
            setShowCreateDialog(false); resetForm();
            clearCyclesCache(); onReloadCycles(); fetchAreas(); onShowToast('Campo criado!', 'success');
        } catch { onShowToast('Erro ao criar campo', 'error'); }
    };

    const openEditDialog = (cycle: any) => {
        setCurrentCycle(cycle);
        setCycleName(cycle.name);
        setCycleDescription(cycle.description || '');
        setCycleIsFree(cycle.isFree || false);
        setCycleSkipDeckLevel(cycle.skipDeckLevel || false);
        setCycleIcon(cycle.icon || 'BookOpen');
        setCycleTopics(cycle.topics || []);
        setShowEditDialog(true);
    };

    const handleEditCycle = async () => {
        if (!currentCycle || !cycleName.trim()) { onShowToast('Digite um nome', 'error'); return; }
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/cycles/${currentCycle._id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    name: cycleName,
                    description: cycleDescription,
                    icon: cycleIcon,
                    topics: cycleTopics.filter(t => t.text.trim()),
                    skipDeckLevel: cycleSkipDeckLevel,
                })
            });
            if (!response.ok) {
                const err = await response.json();
                onShowToast(err.message || 'Erro ao editar campo', 'error'); return;
            }
            setShowEditDialog(false); resetForm(); setCurrentCycle(null);
            clearCyclesCache(); onReloadCycles(); fetchAreas(); onShowToast('Campo atualizado!', 'success');
        } catch { onShowToast('Erro ao editar campo', 'error'); }
    };

    const openDeleteDialog = (cycle: Cycle) => { setCurrentCycle(cycle); setShowDeleteDialog(true); };

    const handleDeleteCycle = async () => {
        if (!currentCycle) return;
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/cycles/${currentCycle._id}`, {
                method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                setShowDeleteDialog(false); clearCyclesCache();
                onReloadCycles(); fetchAreas(); onShowToast('Campo deletado', 'success');
            }
        } catch { onShowToast('Erro ao deletar campo', 'error'); }
    };

    const handleStudySaved = (folderId?: string) => {
        sessionStorage.setItem('graia_theme_context', 'enem');
        navigate(folderId ? `/study/saved?folder=${folderId}` : '/study/saved');
    };

    // ── Buscar áreas com cycles agrupados ──
    // keepAreaId: se fornecido, atualiza currentArea com a área fresca (sem mostrar spinner)
    const fetchAreas = async (keepAreaId?: string) => {
        if (!keepAreaId) setLoadingAreas(true);
        try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch(`${API_BASE_URL}/areas`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                const freshAreas: Area[] = data.areas || [];
                setAreas(freshAreas);
                setCyclesWithoutArea(data.cyclesWithoutArea || []);
                if (keepAreaId) {
                    const updated = freshAreas.find(a => a._id === keepAreaId);
                    if (updated) setCurrentArea(updated);
                }
            }
        } catch (e) {
            console.error('Erro ao carregar áreas:', e);
        } finally {
            if (!keepAreaId) setLoadingAreas(false);
        }
    };

    // ── Handlers de área ──
    const openEditAreaDialog = (area: Area) => {
        setCurrentArea(area);
        setAreaFormName(area.name);
        setAreaFormColor(area.color);
        setAreaFormGridCols(area.gridCols === 3 ? 3 : 2);
        setAreaFormIsVisible(area.isVisible);
        setAreaFormOrder(area.order);
        setShowEditAreaDialog(true);
    };

    const openDeleteAreaDialog = (area: Area) => {
        setCurrentArea(area);
        setShowDeleteAreaDialog(true);
    };

    const resetAreaForm = () => {
        setAreaFormName(''); setAreaFormColor('#003BD1');
        setAreaFormGridCols(2); setAreaFormIsVisible(true); setAreaFormOrder(0);
        setCurrentArea(null);
    };

    const handleCreateArea = async () => {
        if (!areaFormName.trim()) { onShowToast('Digite um nome para a área', 'error'); return; }
        try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch(`${API_BASE_URL}/areas`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    name: areaFormName.trim(),
                    color: areaFormColor,
                    gridCols: areaFormGridCols,
                    order: areas.length + 1
                })
            });
            if (res.ok) {
                setShowCreateAreaDialog(false); resetAreaForm();
                fetchAreas(); onShowToast('Área criada!', 'success');
            } else {
                const err = await res.json();
                onShowToast(err.message || 'Erro ao criar área', 'error');
            }
        } catch { onShowToast('Erro ao criar área', 'error'); }
    };

    const handleEditArea = async () => {
        if (!currentArea || !areaFormName.trim()) { onShowToast('Digite um nome', 'error'); return; }
        try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch(`${API_BASE_URL}/areas/${currentArea._id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    name: areaFormName.trim(),
                    color: areaFormColor,
                    gridCols: areaFormGridCols,
                    isVisible: areaFormIsVisible,
                    order: areaFormOrder
                })
            });
            if (res.ok) {
                setShowEditAreaDialog(false); resetAreaForm();
                fetchAreas(); onShowToast('Área atualizada!', 'success');
            } else {
                const err = await res.json();
                onShowToast(err.message || 'Erro ao editar área', 'error');
            }
        } catch { onShowToast('Erro ao editar área', 'error'); }
    };

    const handleDeleteArea = async () => {
        if (!currentArea) return;
        try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch(`${API_BASE_URL}/areas/${currentArea._id}`, {
                method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                setShowDeleteAreaDialog(false); setCurrentArea(null);
                fetchAreas(); onReloadCycles(); onShowToast('Área deletada', 'success');
            }
        } catch { onShowToast('Erro ao deletar área', 'error'); }
    };

    const handleManageAreasDragEnd = async (event: any) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIdx = areas.findIndex(a => a._id === active.id);
        const newIdx = areas.findIndex(a => a._id === over.id);
        const reordered = arrayMove(areas, oldIdx, newIdx);
        setAreas(reordered);
        try {
            const token = localStorage.getItem('accessToken');
            await fetch(`${API_BASE_URL}/areas/reorder`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ areas: reordered.map((a, i) => ({ _id: a._id, order: i })) })
            });
        } catch { onShowToast('Erro ao reordenar áreas', 'error'); }
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleRemoveCycleFromArea = async (areaId: string, cycleId: string) => {
        try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch(`${API_BASE_URL}/areas/${areaId}/remove-cycle`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ cycleId })
            });
            if (res.ok) {
                fetchAreas(); onReloadCycles(); onShowToast('Campo removido da área', 'success');
            }
        } catch { onShowToast('Erro ao remover campo da área', 'error'); }
    };

    // ── Render do form de criação/edição ──
    const renderCycleForm = (isEdit = false) => (
        <>
            <h3>{isEdit ? 'Editar Campo' : 'Criar Campo de Estudo'}</h3>

            <label className="gcr-field-label">Nome</label>
            <input
                type="text"
                placeholder="Ex: Fundamentos da Medicina"
                value={cycleName}
                onChange={e => setCycleName(e.target.value)}
                autoFocus
            />

            <label className="gcr-field-label">Descrição curta</label>
            <div className="gcr-desc-editor">
                <div className="gcr-desc-toolbar">
                    {[
                        { icon: '•', label: 'Ponto', insert: '\n• ' },
                        { icon: '–', label: 'Traço', insert: '\n– ' },
                        { icon: '→', label: 'Seta', insert: '\n→ ' },
                        { icon: '✓', label: 'Check', insert: '\n✓ ' },
                        { icon: '★', label: 'Star', insert: '\n★ ' },
                    ].map(btn => (
                        <button
                            key={btn.icon}
                            type="button"
                            className="gcr-desc-tool-btn"
                            title={btn.label}
                            onClick={() => {
                                const el = document.getElementById('cycleDescTextarea') as HTMLTextAreaElement;
                                if (!el) return;
                                const start = el.selectionStart;
                                const end = el.selectionEnd;
                                const newVal = cycleDescription.slice(0, start) + btn.insert + cycleDescription.slice(end);
                                setCycleDescription(newVal);
                                setTimeout(() => {
                                    el.focus();
                                    const pos = start + btn.insert.length;
                                    el.setSelectionRange(pos, pos);
                                }, 0);
                            }}
                        >
                            {btn.icon}
                        </button>
                    ))}
                </div>
                <textarea
                    id="cycleDescTextarea"
                    placeholder={"• Adicione uma descrição\n• Use os botões acima para inserir marcadores"}
                    value={cycleDescription}
                    onChange={e => setCycleDescription(e.target.value)}
                    rows={5}
                    className="gcr-desc-textarea"
                />
            </div>

            <label className="gcr-field-label" style={{ marginTop: 16 }}>Ícone do campo</label>
            <IconPicker value={cycleIcon} onChange={setCycleIcon} />

            <div style={{ marginTop: 20 }}>
                <TopicsEditor value={cycleTopics} onChange={setCycleTopics} />
            </div>

            {!isEdit && areas.length > 0 && (
                <div style={{ marginTop: 20 }}>
                    <label className="gcr-field-label">Designar à área (opcional)</label>
                    <select
                        value={cycleAreaId}
                        onChange={e => setCycleAreaId(e.target.value)}
                        className="cycle-area-select"
                    >
                        <option value="">— Outros campos (sem área) —</option>
                        {areas.map(a => (
                            <option key={a._id} value={a._id}>{a.name}</option>
                        ))}
                    </select>
                </div>
            )}

            <div className="form-options-group">
                {!isEdit && (
                    <label className="form-option-row" htmlFor="cycleFreeCheckbox">
                        <input
                            type="checkbox"
                            id="cycleFreeCheckbox"
                            checked={cycleIsFree}
                            onChange={e => setCycleIsFree(e.target.checked)}
                        />
                        <BadgeCheck size={16} className="form-option-icon" />
                        <span className="form-option-text">
                            Gratuito
                            <small>Libera acesso como preview</small>
                        </span>
                    </label>
                )}
                <label
                    className={`form-option-row${(isEdit && (currentCycle?.decks?.length ?? 0) >= 2) ? ' form-option-row--disabled' : ''}`}
                    htmlFor="cycleSkipDeckCheckbox"
                >
                    <input
                        type="checkbox"
                        id="cycleSkipDeckCheckbox"
                        checked={cycleSkipDeckLevel}
                        onChange={e => setCycleSkipDeckLevel(e.target.checked)}
                        disabled={isEdit && (currentCycle?.decks?.length ?? 0) >= 2}
                    />
                    <Layers size={16} className="form-option-icon" />
                    <span className="form-option-text">
                        Ir direto para os Subdecks
                        {isEdit && (currentCycle?.decks?.length ?? 0) >= 2
                            ? <small className="form-option-error">Indisponível: campo com {currentCycle?.decks?.length} decks</small>
                            : <small>Pula a tela de decks intermediária</small>
                        }
                    </span>
                </label>
            </div>

            <div className="dialog-actions">
                <button onClick={() => { isEdit ? setShowEditDialog(false) : setShowCreateDialog(false); resetForm(); }}>
                    Cancelar
                </button>
                <button
                    className="btn-primary"
                    onClick={isEdit ? handleEditCycle : handleCreateCycle}
                >
                    {isEdit ? 'Salvar' : 'Criar'}
                </button>
            </div>
        </>
    );

    return (
        <div className="explore-content-container">

            {/* ========== ABA: CAMPOS DE ESTUDO ========== */}
            <div style={{ display: activeTab === 'content' ? 'block' : 'none' }} className="tab-content-area">
                {loadingAreas ? (
                    <div className="areas-loading"><span className="spinner" style={{ width: 36, height: 36 }} /></div>
                ) : (() => {
                    // Todos os cycles de todas as áreas (para favoritos)
                    const allAreaCycles = areas.flatMap(a => a.cycles);
                    const favCycles = [...allAreaCycles, ...cyclesWithoutArea].filter(c => favs.includes(c._id));
                    // Deduplicar (um cycle pode aparecer em cyclesWithoutArea e numa área ao mesmo tempo em cache stale)
                    const seenFavIds = new Set<string>();
                    const dedupedFavCycles = favCycles.filter(c => { if (seenFavIds.has(c._id)) return false; seenFavIds.add(c._id); return true; });

                    const renderCycleCard = (cycle: any, cycleAreaColor?: string) => (
                        <AccessControl
                            key={cycle._id}
                            entityType="cycle"
                            entityId={cycle._id}
                            entityName={cycle.name}
                            isInterdicted={cycle.isBlocked || false}
                            isPurchased={true}
                            isFree={cycle.isFree || false}
                            isAdmin={isAdmin}
                            isEditor={isEditor}
                            onUpgradeClick={() => { setSelectedCycle(cycle); setShowUpgradeModal(true); }}
                            onInterdictedClick={() => { setSelectedEntityForModal({ type: 'cycle', name: cycle.name }); setShowInterdictedModal(true); }}
                            onNavigate={() => {
                                if (cycle.skipDeckLevel && cycle.singleDeckId) {
                                    const ctx = {
                                        from: 'cycle',
                                        accessMode: 'read',
                                        cycleId: cycle._id,
                                        skipDeckLevel: true,
                                        returnPath: '/dashboard',
                                        returnState: { activePage: 'explore', activeExploreTab: 'content' },
                                        purchased: true,
                                        allowFreeEntry: cycle.isFree || false,
                                    };
                                    sessionStorage.setItem('subdecksContext', JSON.stringify(ctx));
                                    navigate(`/subdecks/${cycle.singleDeckId}`, { state: ctx });
                                } else {
                                    navigate(`/cycles/${cycle._id}/decks`);
                                }
                            }}
                        >
                            <PremiumCycleCard
                                cycle={cycle}
                                isAdmin={isAdmin}
                                areaColor={cycleAreaColor}
                                isFav={favs.includes(cycle._id)}
                                onFavToggle={() => {
                                    if (!favs.includes(cycle._id) && favs.length >= 3) {
                                        onShowToast('Máximo de 3 campos fixados atingido', 'error');
                                        return;
                                    }
                                    toggleFav(cycle._id);
                                }}
                                onEdit={() => openEditDialog(cycle)}
                                onDelete={() => openDeleteDialog(cycle)}
                                onToggleBlock={() => onToggleCycleBlock(cycle._id)}
                                onToggleHide={() => onToggleCycleHide(cycle._id)}
                            />
                        </AccessControl>
                    );

                    const visibleAreas = isAdmin ? areas : areas.filter(a => a.isVisible);
                    const allSortableIds = sortedCycles.map(c => c._id);

                    return (
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                            <SortableContext items={allSortableIds} strategy={rectSortingStrategy}>

                                {/* Seção Fixados */}
                                {dedupedFavCycles.length > 0 && (
                                    <div className="area-section area-section--favorites">
                                        <AreaSeparator area={{ name: 'Fixados', color: '#1473E6' }} />
                                        <div className="gcr-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                                            {dedupedFavCycles.map(cycle => renderCycleCard(cycle, '#1473E6'))}
                                        </div>
                                    </div>
                                )}

                                {/* Áreas */}
                                {visibleAreas.map(area => {
                                    const areaCycles = isAdmin ? area.cycles : area.cycles.filter(c => !c.isHidden);
                                    return (
                                        <div className="area-section" key={area._id}>
                                            <AreaSeparator area={area} />
                                            <div
                                                className="gcr-grid"
                                                style={{ gridTemplateColumns: `repeat(${area.gridCols || 2}, 1fr)` }}
                                            >
                                                {areaCycles.map(cycle => renderCycleCard(cycle, area.color))}
                                            </div>
                                        </div>
                                    );
                                })}

                                {/* Cycles sem área */}
                                {cyclesWithoutArea.length > 0 && (() => {
                                    const noAreaCycles = isAdmin ? cyclesWithoutArea : cyclesWithoutArea.filter(c => !(c as any).isHidden);
                                    if (noAreaCycles.length === 0) return null;
                                    return (
                                        <div className="area-section">
                                            <AreaSeparator area={{ name: 'Outros campos', color: 'var(--text-secondary)' }} />
                                            <div className="gcr-grid">
                                                {noAreaCycles.map(cycle => renderCycleCard(cycle))}
                                            </div>
                                        </div>
                                    );
                                })()}

                            </SortableContext>
                        </DndContext>
                    );
                })()}
            </div>

            {/* ========== ABA: FLASHCARDS SALVOS ========== */}
            <div style={{ display: activeTab === 'saved' ? 'block' : 'none' }} className="tab-content-area">
                <SavedFoldersView
                    onShowToast={onShowToast}
                    onStudySaved={handleStudySaved}
                    onRegisterNewFolder={onRegisterNewFolder}
                />
            </div>

            {/* ========== ABA: ESTUDO PERSONALIZADO ========== */}
            <div style={{ display: activeTab === 'custom' ? 'block' : 'none' }} className="tab-content-area">
                <CustomStudy cycles={cycles} onShowToast={onShowToast} />
            </div>

            {/* ========== DIALOGS ========== */}
            {showCreateDialog && (
                <div className="dialog-overlay" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
                    <div className="dialog gcr-dialog-wide" onClick={e => e.stopPropagation()}>
                        {renderCycleForm(false)}
                    </div>
                </div>
            )}

            {showEditDialog && (
                <div className="dialog-overlay" onClick={() => { setShowEditDialog(false); resetForm(); }}>
                    <div className="dialog gcr-dialog-wide" onClick={e => e.stopPropagation()}>
                        {renderCycleForm(true)}
                    </div>
                </div>
            )}

            {showDeleteDialog && (
                <div className="dialog-overlay" onClick={() => setShowDeleteDialog(false)}>
                    <div className="dialog" onClick={e => e.stopPropagation()}>
                        <h3>Deletar Campo</h3>
                        <p>Tem certeza que deseja deletar "{currentCycle?.name}"?</p>
                        <p style={{ fontSize: '13px', color: '#999' }}>Os decks não serão deletados.</p>
                        <div className="dialog-actions">
                            <button onClick={() => setShowDeleteDialog(false)}>Cancelar</button>
                            <button className="btn-danger" onClick={handleDeleteCycle}>Deletar</button>
                        </div>
                    </div>
                </div>
            )}

            {showUpgradeModal && selectedCycle && (
                <UpgradeModal
                    isOpen={showUpgradeModal}
                    onClose={() => { setShowUpgradeModal(false); setSelectedCycle(null); }}
                    entityType="cycle"
                    entityName={selectedCycle.name}
                    entityId={selectedCycle._id}
                />
            )}

            {showInterdictedModal && selectedEntityForModal && (
                <InterdictedModal
                    isOpen={showInterdictedModal}
                    onClose={() => { setShowInterdictedModal(false); setSelectedEntityForModal(null); }}
                    entityType={selectedEntityForModal.type}
                    entityName={selectedEntityForModal.name}
                />
            )}

            {/* ========== DIALOG: CRIAR ÁREA ========== */}
            {showCreateAreaDialog && (
                <div className="dialog-overlay" onClick={() => { setShowCreateAreaDialog(false); resetAreaForm(); }}>
                    <div className="dialog" onClick={e => e.stopPropagation()}>
                        <h3>Criar Área</h3>
                        <label className="gcr-field-label">Nome</label>
                        <input
                            type="text"
                            placeholder="Ex: Humanas, Exatas…"
                            value={areaFormName}
                            onChange={e => setAreaFormName(e.target.value)}
                            autoFocus
                        />
                        <label className="gcr-field-label" style={{ marginTop: 14 }}>Cor predominante</label>
                        <AreaColorPicker value={areaFormColor} onChange={setAreaFormColor} />
                        <label className="gcr-field-label" style={{ marginTop: 14 }}>Colunas do grid</label>
                        <div className="area-gridcols-row">
                            {([2, 3] as const).map(n => (
                                <button
                                    key={n}
                                    type="button"
                                    className={`area-gridcols-btn ${areaFormGridCols === n ? 'active' : ''}`}
                                    onClick={() => setAreaFormGridCols(n)}
                                >
                                    {n} colunas
                                </button>
                            ))}
                        </div>
                        <div className="dialog-actions">
                            <button onClick={() => { setShowCreateAreaDialog(false); resetAreaForm(); }}>Cancelar</button>
                            <button className="btn-primary" onClick={handleCreateArea}>Criar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ========== DIALOG: EDITAR ÁREA ========== */}
            {showEditAreaDialog && currentArea && (() => {
                const currentAreaIds = new Set(currentArea.cycles.map(c => c._id));
                const available = [
                    ...cyclesWithoutArea,
                    ...areas.filter(a => a._id !== currentArea._id).flatMap(a => a.cycles)
                ].filter(c => !currentAreaIds.has(c._id));

                const handleAddFromEdit = async () => {
                    if (!addCycleSelectedId) return;
                    const token = localStorage.getItem('accessToken');
                    try {
                        const res = await fetch(`${API_BASE_URL}/areas/${currentArea._id}/add-cycle`, {
                            method: 'PATCH',
                            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                            body: JSON.stringify({ cycleId: addCycleSelectedId })
                        });
                        if (res.ok) { setAddCycleSelectedId(''); fetchAreas(currentArea._id); }
                        else { const err = await res.json(); onShowToast(err.message || 'Erro', 'error'); }
                    } catch { onShowToast('Erro ao adicionar campo', 'error'); }
                };

                const handleRemoveFromEdit = async (cycleId: string) => {
                    const token = localStorage.getItem('accessToken');
                    try {
                        const res = await fetch(`${API_BASE_URL}/areas/${currentArea._id}/remove-cycle`, {
                            method: 'PATCH',
                            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                            body: JSON.stringify({ cycleId })
                        });
                        if (res.ok) fetchAreas(currentArea._id);
                    } catch { onShowToast('Erro ao remover campo', 'error'); }
                };

                return (
                    <div className="dialog-overlay" onClick={() => { setShowEditAreaDialog(false); resetAreaForm(); }}>
                        <div className="dialog gcr-dialog-wide" onClick={e => e.stopPropagation()}>
                            <h3>Editar Área</h3>
                            <label className="gcr-field-label">Nome</label>
                            <input
                                type="text"
                                value={areaFormName}
                                onChange={e => setAreaFormName(e.target.value)}
                                autoFocus
                            />
                            <label className="gcr-field-label" style={{ marginTop: 14 }}>Cor predominante</label>
                            <AreaColorPicker value={areaFormColor} onChange={setAreaFormColor} />
                            <label className="gcr-field-label" style={{ marginTop: 14 }}>Colunas do grid</label>
                            <div className="area-gridcols-row">
                                {([2, 3] as const).map(n => (
                                    <button
                                        key={n}
                                        type="button"
                                        className={`area-gridcols-btn ${areaFormGridCols === n ? 'active' : ''}`}
                                        onClick={() => setAreaFormGridCols(n)}
                                    >
                                        {n} colunas
                                    </button>
                                ))}
                            </div>
                            <label className="gcr-field-label" style={{ marginTop: 14 }}>Ordem de exibição</label>
                            <input
                                type="number"
                                min={1}
                                value={areaFormOrder}
                                onChange={e => setAreaFormOrder(Number(e.target.value))}
                            />

                            {/* Campos desta área */}
                            <label className="gcr-field-label" style={{ marginTop: 18 }}>Campos nesta área</label>
                            <div className="edit-area-cycles-list">
                                {currentArea.cycles.length === 0 ? (
                                    <p className="edit-area-cycles-empty">Nenhum campo nesta área ainda.</p>
                                ) : (
                                    currentArea.cycles.map(c => (
                                        <div key={c._id} className="edit-area-cycle-row">
                                            <span className="edit-area-cycle-name">{c.name}</span>
                                            <button
                                                type="button"
                                                className="edit-area-cycle-remove"
                                                onClick={() => handleRemoveFromEdit(c._id)}
                                                title="Remover da área"
                                            >
                                                <X size={13} />
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>
                            {available.length > 0 && (
                                <div className="edit-area-add-row">
                                    <select
                                        value={addCycleSelectedId}
                                        onChange={e => setAddCycleSelectedId(e.target.value)}
                                        className="edit-area-add-select"
                                    >
                                        <option value="">— Adicionar campo —</option>
                                        {available.map(c => (
                                            <option key={c._id} value={c._id}>{c.name}</option>
                                        ))}
                                    </select>
                                    <button
                                        type="button"
                                        className="btn-primary edit-area-add-btn"
                                        onClick={handleAddFromEdit}
                                        disabled={!addCycleSelectedId}
                                    >
                                        Adicionar
                                    </button>
                                </div>
                            )}

                            {/* Visibilidade */}
                            <label className="area-visible-row" htmlFor="areaVisibleCheck" style={{ marginTop: 16 }}>
                                <input
                                    type="checkbox"
                                    id="areaVisibleCheck"
                                    checked={areaFormIsVisible}
                                    onChange={e => setAreaFormIsVisible(e.target.checked)}
                                />
                                <div className="area-visible-label-text">
                                    <span>Área visível para alunos</span>
                                    <small>Quando desmarcado, apenas admins visualizam</small>
                                </div>
                            </label>

                            <div className="dialog-actions">
                                <button onClick={() => { setShowEditAreaDialog(false); resetAreaForm(); }}>Cancelar</button>
                                <button className="btn-primary" onClick={handleEditArea}>Salvar</button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* ========== DIALOG: DELETAR ÁREA ========== */}
            {showDeleteAreaDialog && currentArea && (
                <div className="dialog-overlay" onClick={() => { setShowDeleteAreaDialog(false); setCurrentArea(null); }}>
                    <div className="dialog" onClick={e => e.stopPropagation()}>
                        <h3>Deletar Área</h3>
                        <p>Tem certeza que deseja deletar "{currentArea.name}"?</p>
                        <p style={{ fontSize: 13, color: '#999', marginTop: 6 }}>
                            Os campos desta área serão movidos para "Outros campos" — não serão deletados.
                        </p>
                        <div className="dialog-actions">
                            <button onClick={() => { setShowDeleteAreaDialog(false); setCurrentArea(null); }}>Cancelar</button>
                            <button className="btn-danger" onClick={handleDeleteArea}>Deletar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ========== MODAL: GERENCIAR ÁREAS ========== */}
            {showManageAreasModal && (
                <div className="dialog-overlay" onClick={() => setShowManageAreasModal(false)}>
                    <div className="dialog manage-areas-modal" onClick={e => e.stopPropagation()}>
                        <div className="manage-areas-header">
                            <h3>Gerenciar Áreas</h3>
                            <button className="manage-areas-close" onClick={() => setShowManageAreasModal(false)}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="manage-areas-list">
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={handleManageAreasDragEnd}
                            >
                                <SortableContext items={areas.map(a => a._id)} strategy={verticalListSortingStrategy}>
                                    {areas.map(area => (
                                        <SortableAreaItem
                                            key={area._id}
                                            area={area}
                                            onEdit={() => { setShowManageAreasModal(false); openEditAreaDialog(area); }}
                                            onDelete={() => { setShowManageAreasModal(false); openDeleteAreaDialog(area); }}
                                        />
                                    ))}
                                </SortableContext>
                            </DndContext>
                            {areas.length === 0 && (
                                <p className="manage-areas-empty">Nenhuma área criada ainda.</p>
                            )}
                        </div>
                        <div className="manage-areas-footer">
                            <button
                                className="btn-primary manage-areas-new-btn"
                                onClick={() => { setShowManageAreasModal(false); setShowCreateAreaDialog(true); }}
                            >
                                <Plus size={15} /> Nova área
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default ExploreContent;